import { FormInput, TabsNavigation } from 'components/molecules';
import { TopBar } from 'components/organisms';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig, FormikHelpers } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { DEMO_MODE, IAccountMutationForm } from 'services/accounts/types';
import { CLIENT_ACCESS_MODE, CURRENCY } from 'services/types';
import { II18nextPrefix } from 'types/components/components.interface';
import { formatDateToInput } from 'utils/global/date.utils';
import { mixed, object, string } from 'yup';
import { IFormikProps, IFormProps } from '../types';
import AccountFinances from './tabs/AccountFinances';
import AccountServices from './tabs/AccountServices';
import AccountSettings from './tabs/AccountSettings';
import AccountSettlementMethods from './tabs/AccountSettlementMethods';
import AccountSmartlinkDomains from './tabs/AccountSmartlinkDomains';
import AccountStatementTemplates from './tabs/AccountStatementTemplates';

export enum AccountTabs {
  finances,
  settings,
  settlementMethods,
  services,
  smartlinkDomains,
  statementTemplates,
}

export const accountInitialValues: IAccountMutationForm = {
  id: '',
  accountName: '',
  endpoint: '',
  clientEndpoint: '',
  apiKey: '',
  email: '',
  firstName: '',
  lastName: '',
  name: '',
  fullName: '',
  phoneNumberPrefix: '',
  phoneNumber: '',
  availableFrom: formatDateToInput(new Date(), true),
  availableTo: '',
  withEndDate: false,
  defaultCurrencyId: CURRENCY.PLN,
  deliveryCode: 1,
  clientAccessModeId: CLIENT_ACCESS_MODE.Passive,
  isrcGenerationMode: 1,
  showOwnersLogoAsMain: false,
  clientModuleAccess: false,
  enableDigitalDelivery: false,
  availableSettlementMethods: [],
  availableServices: [],
  availableStatementTemplates: [],
  availableStatementTemplateIds: [],
  smartlinkDomains: [
    {
      id: '43f130b2-8949-4ad3-83f9-f515211bf928',
      name: 'magelup.lnk.to',
    },
  ],
  address: {
    street: '',
    houseNumber: '',
    flatNumber: '',
    zipCode: '',
    city: '',
    countryAlfa2: '',
  },
  taxOffice: {
    name: '',
    street: '',
    houseNumber: '',
    flatNumber: '',
    zipCode: '',
    city: '',
    countryAlfa2: '',
  },
  companyId: '',
  companyCountryAlfa2: '',
  bankAccountNumber: '',
  bankName: '',
  swift: '',
  isDemo: false,
  demoModeId: DEMO_MODE.client,
};

export const accountValidator = object().shape({
  accountName: string().required(() => i18next.t('formik-error.required')),
  endpoint: string().required(() => i18next.t('formik-error.required')),
  clientEndpoint: string().required(() => i18next.t('formik-error.required')),
  apiKey: string().required(() => i18next.t('formik-error.required')),

  availableSettlementMethods: mixed().nullable(),
  availableServices: mixed().nullable(),
  smartlinkDomains: mixed().nullable(),
});

export interface IAccountForm
  extends
    Omit<FormikConfig<IAccountMutationForm>, 'onSubmit'>,
    IFormProps,
    II18nextPrefix {
  onSubmit?: (
    values: IAccountMutationForm,
    helpers: FormikHelpers<IAccountMutationForm>,
  ) => void | Promise<any>;
}

const AccountForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  initialValues = accountInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  status: formStatus,
  ...props
}: IAccountForm) => {
  const { t } = useTranslation();

  return (
    <Formik
      {...props}
      validateOnChange={false}
      validateOnBlur={false}
      validateOnMount={false}
      initialStatus={formStatus}
      enableReinitialize={props.enableReinitialize ?? true}
      initialValues={initialValues}
      validationSchema={accountValidator}
      onSubmit={(values, helpers) => {
        props.onSubmit?.(values, helpers);
      }}>
      {({
        setFieldValue,
        values,
        status,
        errors,
      }: IFormikProps<IAccountMutationForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={formStatus?.displayMode ? tittle : undefined}
              path={backPath ?? 'account'}
              topbarContent={
                <div className=' w-full flex justify-end'>
                  {formStatus?.displayMode ? null : (
                    <div className='w-48'>
                      <FormInput.Text
                        name='accountName'
                        label={t('input.label.accountName')}
                        autoFocus
                      />
                    </div>
                  )}
                  <div className='flex items-center gap-2 w-full justify-end'>
                    <div className='w-64'>
                      <FormInput.Text
                        name='endpoint'
                        label={t('input.label.apiEndpoint')}
                      />
                    </div>
                    <div className='w-64'>
                      <FormInput.Text
                        name='clientEndpoint'
                        label={t('input.label.clientEndpoint')}
                      />
                    </div>
                    <div className='w-64'>
                      <FormInput.Password
                        name='apiKey'
                        label={t('input.label.apiKey')}
                      />
                    </div>
                    <div
                      className={`ml-4 ${
                        formStatus?.displayMode ? 'pointer-events-none' : ''
                      }`}>
                      <FormInput.Switch name='withLicense' label={'Licence'} />
                    </div>
                    {topbarContent}
                  </div>
                </div>
              }>
              <TabsNavigation
                tabs={[
                  {
                    value: AccountTabs.finances,
                    name: 'finances',
                  },
                  {
                    value: AccountTabs.settings,
                    name: 'settings',
                  },
                  {
                    value: AccountTabs.settlementMethods,
                    name: 'settlementMethods',
                  },
                  {
                    value: AccountTabs.services,
                    name: 'services',
                  },
                  {
                    value: AccountTabs.smartlinkDomains,
                    name: 'smartlinkDomains',
                  },
                  {
                    value: AccountTabs.statementTemplates,
                    name: 'statementTemplates',
                  },
                ]}
                i18nextPrefix={i18nextPrefix}
                disabled={[
                  ...(disableTabs ?? []),
                  ...(values.withLicense
                    ? []
                    : [
                        AccountTabs.finances,
                        AccountTabs.settings,
                        AccountTabs.settlementMethods,
                        AccountTabs.services,
                        AccountTabs.smartlinkDomains,
                        AccountTabs.statementTemplates,
                      ]),
                ]}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

AccountForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'finances', element: <AccountFinances /> },
    { path: 'settings', element: <AccountSettings /> },
    { path: 'settlementMethods', element: <AccountSettlementMethods /> },
    { path: 'services', element: <AccountServices /> },
    { path: 'smartlinkDomains', element: <AccountSmartlinkDomains /> },
    { path: 'statementTemplates', element: <AccountStatementTemplates /> },
  ].filter(children => !disabledChildren?.includes(children.path));

export default AccountForm;
